import type { Id } from "../../../convex/_generated/dataModel";

export type StagedEdit = {
  editId: string;
  target:
    | "sectionDescription"
    | "sectionExamples"
    | "sectionFormatTemplate"
    | "tagRule"
    | "sectionTags";
  action: "replace" | "remove" | "add";
  newValue?: string;
  exampleIndex?: number;
  ruleId?: string;
  tagId?: string;
  agentId?: string;
  agentName?: string;
  sourceTurnId?: string;
  sourceChangeIdx?: number;
  rationale?: string;
  summary?: string;
  createdAt: number;
};

export type StagedRow = {
  edits: StagedEdit[];
  previewContent?: string;
  previewGeneratedAt?: number;
};

export type TemplateSectionLike = {
  _id: Id<"templateSections">;
  name: string;
  description?: string;
  examples?: string[];
  adhereToFormatting?: boolean;
  formatTemplate?: string;
  doubleSpaceOutput?: boolean;
  allowAssessment?: boolean;
  tagIds?: Id<"ruleTags">[];
};

export type GlobalRuleLike = { _id: Id<"globalRules"> };

export function detectStaleEdits(
  row: StagedRow,
  section: TemplateSectionLike,
  rules: GlobalRuleLike[] | null | undefined,
): Map<string, string> {
  const errors = new Map<string, string>();
  const examples = section.examples || [];
  const ruleIds = new Set((rules || []).map((r) => String(r._id)));
  const sectionTagIds = new Set(
    (section.tagIds || []).map((id) => String(id)),
  );
  for (const e of row.edits || []) {
    if (e.target === "sectionExamples") {
      if (e.action === "replace" || e.action === "remove") {
        const idx = typeof e.exampleIndex === "number" ? e.exampleIndex - 1 : -1;
        if (idx < 0 || idx >= examples.length) {
          errors.set(
            e.editId,
            `Example ${e.exampleIndex} no longer exists — remove or re-stage this edit.`,
          );
        }
      }
    } else if (e.target === "tagRule") {
      if (e.action === "add") {
        if (!e.tagId || !sectionTagIds.has(String(e.tagId))) {
          errors.set(
            e.editId,
            "The target tag is no longer attached to this section — remove this edit.",
          );
        } else if (typeof e.newValue !== "string" || !e.newValue.trim()) {
          errors.set(
            e.editId,
            "The new rule has no content — remove this edit.",
          );
        }
      } else if (e.ruleId && !ruleIds.has(String(e.ruleId))) {
        errors.set(
          e.editId,
          "The target rule no longer exists — remove this edit.",
        );
      }
    } else if (e.target === "sectionTags") {
      // Re-check add/remove validity against the latest section tagIds.
      // Apply prior staged sectionTags edits (in createdAt order) so a
      // sequence like add(X) then remove(X) validates correctly.
      const projected = new Set(sectionTagIds);
      const ordered = [...(row.edits || [])].sort(
        (a, b) => a.createdAt - b.createdAt,
      );
      for (const prior of ordered) {
        if (prior.editId === e.editId) break;
        if (prior.target !== "sectionTags") continue;
        if (errors.has(prior.editId)) continue;
        if (!prior.tagId) continue;
        if (prior.action === "add") projected.add(String(prior.tagId));
        else if (prior.action === "remove")
          projected.delete(String(prior.tagId));
      }
      if (!e.tagId) {
        errors.set(e.editId, "Missing tag id — remove this edit.");
      } else if (e.action === "add" && projected.has(String(e.tagId))) {
        errors.set(
          e.editId,
          "That tag is already attached to this section — remove this edit.",
        );
      } else if (e.action === "remove" && !projected.has(String(e.tagId))) {
        errors.set(
          e.editId,
          "That tag is no longer attached to this section — remove this edit.",
        );
      }
    }
  }
  return errors;
}

export type UpdateSectionArgs = {
  id: Id<"templateSections">;
  userId: Id<"users">;
  name: string;
  description: string;
  examples: string[];
  adhereToFormatting?: boolean;
  formatTemplate?: string;
  doubleSpaceOutput?: boolean;
  allowAssessment?: boolean;
  tagIds?: Id<"ruleTags">[];
  provenance: { source: "agent"; agentId?: Id<"agents"> };
};

export type UpdateRuleArgs = {
  id: Id<"globalRules">;
  userId: Id<"users">;
  content: string;
  provenance: { source: "agent"; agentId?: Id<"agents"> };
};

export type RemoveRuleArgs = {
  id: Id<"globalRules">;
  userId: Id<"users">;
};

export type CreateRuleArgs = {
  userId: Id<"users">;
  content: string;
  tagId: Id<"ruleTags">;
  provenance: { source: "agent"; agentId?: Id<"agents"> };
};

export type ApplySectionContentEditArgs = {
  id: Id<"recordingSessions">;
  userId: Id<"users">;
  sectionTitle: string;
  newContent: string;
  provenance?: { source: "user" | "agent"; agentId?: Id<"agents"> };
  summaryOverride?: string;
};

export type RemoveStagedEditArgs = {
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
  templateSectionId: Id<"templateSections">;
  editId: string;
};

export type ClearStagedArgs = {
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
  templateSectionId: Id<"templateSections">;
};

export type ClearPreviewArgs = {
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
  templateSectionId: Id<"templateSections">;
};

export type ApplyMutations = {
  updateSection: (args: UpdateSectionArgs) => Promise<unknown>;
  updateRule: (args: UpdateRuleArgs) => Promise<unknown>;
  removeRule: (args: RemoveRuleArgs) => Promise<unknown>;
  createRule: (args: CreateRuleArgs) => Promise<unknown>;
  applySectionContentEdit: (args: ApplySectionContentEditArgs) => Promise<unknown>;
  removeStagedEdit: (args: RemoveStagedEditArgs) => Promise<unknown>;
  clearStaged: (args: ClearStagedArgs) => Promise<unknown>;
  clearPreview: (args: ClearPreviewArgs) => Promise<unknown>;
};

export type ApplyResult = {
  appliedEditIds: string[];
  errors: Record<string, string>;
  ok: boolean;
};

/**
 * Shared apply logic used by both Section Assistant and the Dashboard banner.
 *
 * Strategy: per-item validation + continue-on-error, with a "previewed =
 * committed" guarantee.
 *
 * 1. Stale items (missing example index, deleted rule, out-of-range index)
 *    are marked with per-item errors and NOT applied. The rest of the batch
 *    proceeds.
 * 2. All non-stale section-level edits are merged in memory and committed
 *    in a single `updateSection` call. If that call fails, every edit it
 *    represented is marked failed; other (rule) edits still proceed.
 * 3. Rule edits are applied one at a time; per-item failures are recorded
 *    and do not stop subsequent rule edits.
 * 4. Only when EVERY staged edit applied cleanly is `previewContent` written
 *    into the document and the staged row cleared. This preserves the
 *    "what you previewed is what gets committed" guarantee.
 * 5. If some edits failed, only the successfully applied edits are removed
 *    from the staged row (one-by-one via `removeStagedEdit`). The preview
 *    becomes stale relative to the remaining items, so the user is expected
 *    to fix the failed items, re-preview, then Apply again.
 */
export async function applyStagedEdits(opts: {
  row: StagedRow;
  section: TemplateSectionLike;
  rules: GlobalRuleLike[] | null | undefined;
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
  mutations: ApplyMutations;
}): Promise<ApplyResult> {
  const { row, section, rules, userId, sessionId, mutations } = opts;
  const errors: Record<string, string> = {};
  const appliedEditIds: string[] = [];

  if (!row.previewContent) {
    return { appliedEditIds: [], errors: {}, ok: false };
  }

  // 1) Mark stale edits with per-item errors. They will not be applied.
  const stale = detectStaleEdits(row, section, rules);
  stale.forEach((msg, editId) => {
    errors[editId] = msg;
  });

  const sorted = [...row.edits].sort((a, b) => a.createdAt - b.createdAt);
  const sectionLevelEdits = sorted.filter((e) => e.target !== "tagRule");
  const ruleEdits = sorted.filter((e) => e.target === "tagRule");

  // 2) Merge non-stale section-level edits in memory; record per-item
  //    out-of-range errors without aborting the batch.
  let descChanged = false;
  let formatChanged = false;
  let examplesChanged = false;
  let tagsChanged = false;
  let nextDescription = section.description;
  let nextFormat = section.formatTemplate;
  let nextExamples = [...(section.examples || [])];
  let nextTagIds: Id<"ruleTags">[] = [...((section.tagIds as Id<"ruleTags">[]) || [])];
  const provenanceAgents = new Set<string>();
  const includedSectionEdits: StagedEdit[] = [];

  for (const e of sectionLevelEdits) {
    if (errors[e.editId]) continue;
    if (e.agentId) provenanceAgents.add(String(e.agentId));
    if (e.target === "sectionDescription" && typeof e.newValue === "string") {
      nextDescription = e.newValue;
      descChanged = true;
      includedSectionEdits.push(e);
    } else if (
      e.target === "sectionFormatTemplate" &&
      typeof e.newValue === "string"
    ) {
      nextFormat = e.newValue;
      formatChanged = true;
      includedSectionEdits.push(e);
    } else if (e.target === "sectionTags") {
      if (!e.tagId) {
        errors[e.editId] = "Missing tag id.";
        continue;
      }
      const tid = String(e.tagId);
      const currentIds = nextTagIds.map((id) => String(id));
      if (e.action === "add") {
        if (currentIds.includes(tid)) {
          errors[e.editId] =
            "That tag is already attached to this section.";
          continue;
        }
        nextTagIds.push(e.tagId as Id<"ruleTags">);
        tagsChanged = true;
        includedSectionEdits.push(e);
      } else if (e.action === "remove") {
        if (!currentIds.includes(tid)) {
          errors[e.editId] =
            "That tag is not currently attached to this section.";
          continue;
        }
        nextTagIds = nextTagIds.filter((id) => String(id) !== tid);
        tagsChanged = true;
        includedSectionEdits.push(e);
      } else {
        errors[e.editId] = "Unsupported action for sectionTags.";
      }
    } else if (e.target === "sectionExamples") {
      if (e.action === "add" && typeof e.newValue === "string") {
        nextExamples.push(e.newValue);
        examplesChanged = true;
        includedSectionEdits.push(e);
      } else if (e.action === "remove" && typeof e.exampleIndex === "number") {
        const idx = e.exampleIndex - 1;
        if (idx >= 0 && idx < nextExamples.length) {
          nextExamples = nextExamples.filter((_, i) => i !== idx);
          examplesChanged = true;
          includedSectionEdits.push(e);
        } else {
          errors[e.editId] = "Example index out of range.";
        }
      } else if (
        e.action === "replace" &&
        typeof e.exampleIndex === "number" &&
        typeof e.newValue === "string"
      ) {
        const idx = e.exampleIndex - 1;
        if (idx >= 0 && idx < nextExamples.length) {
          nextExamples[idx] = e.newValue;
          examplesChanged = true;
          includedSectionEdits.push(e);
        } else {
          errors[e.editId] = "Example index out of range.";
        }
      }
    }
  }

  // 3) Commit the merged section update (single mutation for all included
  //    section-level edits). On failure, mark all of them errored.
  const provAgentId =
    provenanceAgents.size === 1 ? Array.from(provenanceAgents)[0] : undefined;
  let sectionCommitted = true;
  if (
    includedSectionEdits.length > 0 &&
    (descChanged || formatChanged || examplesChanged || tagsChanged)
  ) {
    try {
      await mutations.updateSection({
        id: section._id,
        userId,
        name: section.name,
        description: nextDescription ?? "",
        examples: nextExamples,
        adhereToFormatting: section.adhereToFormatting,
        formatTemplate: nextFormat,
        doubleSpaceOutput: section.doubleSpaceOutput,
        allowAssessment: section.allowAssessment,
        tagIds: nextTagIds,
        provenance: {
          source: "agent",
          ...(provAgentId ? { agentId: provAgentId as Id<"agents"> } : {}),
        },
      });
      for (const e of includedSectionEdits) appliedEditIds.push(e.editId);
    } catch (err) {
      sectionCommitted = false;
      const msg = err instanceof Error ? err.message : "Section update failed.";
      for (const e of includedSectionEdits) errors[e.editId] = msg;
    }
  }

  // 4) Apply rule edits one at a time with continue-on-error.
  for (const e of ruleEdits) {
    if (errors[e.editId]) continue;
    try {
      const provAgent = e.agentId
        ? { agentId: e.agentId as unknown as Id<"agents"> }
        : {};
      if (e.action === "add") {
        if (!e.tagId) {
          errors[e.editId] = "Missing tag id for new rule.";
          continue;
        }
        if (typeof e.newValue !== "string" || !e.newValue.trim()) {
          errors[e.editId] = "New rule content is empty.";
          continue;
        }
        await mutations.createRule({
          userId,
          content: e.newValue,
          tagId: e.tagId as unknown as Id<"ruleTags">,
          provenance: { source: "agent", ...provAgent },
        });
      } else if (!e.ruleId) {
        errors[e.editId] = "Missing rule id.";
        continue;
      } else if (e.action === "remove") {
        await mutations.removeRule({
          id: e.ruleId as unknown as Id<"globalRules">,
          userId,
        });
      } else if (typeof e.newValue === "string") {
        await mutations.updateRule({
          id: e.ruleId as unknown as Id<"globalRules">,
          userId,
          content: e.newValue,
          provenance: { source: "agent", ...provAgent },
        });
      }
      appliedEditIds.push(e.editId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Rule update failed.";
      errors[e.editId] = msg;
    }
  }

  // 5) Only when every staged edit applied cleanly do we commit the preview
  //    to the document and clear the row. Otherwise preserve failed items.
  const allClean =
    Object.keys(errors).length === 0 &&
    appliedEditIds.length === row.edits.length &&
    sectionCommitted;

  if (allClean) {
    try {
      await mutations.applySectionContentEdit({
        id: sessionId,
        userId,
        sectionTitle: section.name,
        newContent: row.previewContent,
        provenance: {
          source: "agent",
          ...(provAgentId ? { agentId: provAgentId as Id<"agents"> } : {}),
        },
        summaryOverride: "Applied staged AI changes",
      });
      await mutations.clearStaged({
        userId,
        sessionId,
        templateSectionId: section._id,
      });
      return { appliedEditIds, errors: {}, ok: true };
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Document update failed — staged changes were preserved.";
      for (const e of row.edits) {
        if (!errors[e.editId]) errors[e.editId] = msg;
      }
      return { appliedEditIds: [], errors, ok: false };
    }
  }

  // 6) Some items failed: remove only the successfully applied items from
  //    the staged row, keeping failed items for retry. Do NOT write the
  //    document — the preview no longer matches what would commit. Also
  //    clear `previewContent` so the user must re-preview before Apply,
  //    preserving the "previewed = committed" guarantee.
  for (const editId of appliedEditIds) {
    try {
      await mutations.removeStagedEdit({
        userId,
        sessionId,
        templateSectionId: section._id,
        editId,
      });
    } catch {
      // Best-effort cleanup; failures here are non-fatal — the user can
      // discard or re-stage.
    }
  }
  try {
    await mutations.clearPreview({
      userId,
      sessionId,
      templateSectionId: section._id,
    });
  } catch {
    // Non-fatal — UI still surfaces per-item errors that block Apply.
  }

  return { appliedEditIds, errors, ok: false };
}
